import {
  useState,
} from 'react'
import type {
  PipedreamAppConnectionResponse,
} from '../client'
import {
  AccountNameField,
} from './AccountNameField'
import {
  PairingModalShell,
} from './pairing/PairingModalShell'
import {
  validateNewInstanceLabel,
} from '../utils/integrationLabels'
import {
  pipedreamAccountName,
} from '../utils/pipedreamAccounts'

/**
 * Names another account of a Pipedream app the user already has, then starts
 * its connect (GET-17441: a shared RingCentral mailbox beside the user's own).
 *
 * The name is required because it is how the assistant tells the accounts
 * apart, the same rule as the native "Add another account" modal.
 *
 * The sign-out hint is there because the provider's sign-in page usually
 * reuses the browser's session, which hands back the account already
 * connected. The backend refuses that duplicate, and this says how to avoid it
 * before the user meets the refusal.
 */
export function PipedreamAddAccountModal({
  appName,
  accounts,
  onSubmit,
  onClose,
}: {
  appName: string
  accounts: PipedreamAppConnectionResponse[]
  onSubmit: (label: string) => void
  onClose: () => void
}) {
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    const invalid = validateNewInstanceLabel(
      accounts.map((account) => account.label),
      label,
    )
    if (invalid) {
      setError(invalid)
      return
    }
    // Synchronously, from the click: the connect opens its popup here, and a
    // popup opened after an await is blocked.
    onSubmit(label.trim())
  }

  return (
    <PairingModalShell
      isOpen
      onClose={onClose}
      closeAriaLabel="Close"
      title={`Add another ${appName} account`}
    >
      <div className="space-y-4">
        <AccountNameField
          id="pipedream-account-label"
          value={label}
          onChange={(value) => { setLabel(value); setError(null) }}
          existingLabels={accounts.map(pipedreamAccountName)}
          inputClassName="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
        />

        <p className="text-sm text-gray-600">
          {appName} may sign you straight back into the account you already
          connected. If it does, sign out of {appName} first, or open this page
          in a private window.
        </p>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
          >
            Add account
          </button>
        </div>
      </div>
    </PairingModalShell>
  )
}
